// ChatPanelHost — projects the shell model into the docked ChatPanel
// (redesign step 1: the chat lives here, beside the main view).
//
// The two tabs ARE the dock's two slots, so the existing frame state decides
// which shows and survives a reload (dockPreference.ts): "Assistant" =
// `assistant` (the conversation the panel holds, chatDock.ts), the second
// tab = `panel` (ContextPanel: the chat's materials, sources and files, or
// one focused entity). Every "show it next to the chat" (`show-in-panel`)
// therefore lands on that tab by itself. Switching tabs is `summon-slot`;
// close is `close-secondary`.

export { ChatPanelHost };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import {
    ChatPanel,
    type ChatPanelView,
} from "../../../components/ChatPanel/ChatPanel.js";
import { ConversationPicker } from "../../../components/ChatPanel/ConversationPicker.js";
import { EmptyState } from "../../../components/EmptyState/EmptyState.js";
import { useNow } from "../../../hooks/useNow.js";
import { railListStatus, rowsForMode } from "../../conversationSummary.js";
import { ConversationCentre } from "../Centre/ConversationCentre.js";
import { useAppShell } from "../context.js";
import { conversationRowsOf } from "../conversationRows.js";
import { ContextPanel } from "./ContextPanel.js";

const ChatPanelHost = ({
    view,
}: {
    readonly view: ChatPanelView;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const nowMs = useNow(60_000);
    const {
        frame,
        dispatch,
        refFor,
        mkConversationId,
        activeConversationId,
        conversationSummaries,
        conversationList,
        retryConversationList,
        openConversation,
    } = useAppShell();
    const untitled = t`New conversation`;
    const rows = conversationRowsOf(
        conversationSummaries,
        activeConversationId,
        nowMs,
        i18n.locale,
        untitled,
    );
    const current = rows.find((row) => row.active);
    const { panelFocus } = frame;
    const newConversation = () =>
        dispatch({ type: "new-tab", conversationId: mkConversationId() });

    return (
        <ChatPanel
            view={view}
            contextLabel={
                panelFocus.type === "entity"
                    ? (refFor(panelFocus.ref.id)?.title ?? panelFocus.ref.title)
                    : t`Context`
            }
            onShowView={(next) =>
                dispatch({
                    type: "summon-slot",
                    slot: next === "assistant" ? "assistant" : "panel",
                })
            }
            onClose={() => dispatch({ type: "close-secondary" })}
            head={
                <ConversationPicker
                    title={current?.title ?? untitled}
                    status={railListStatus(conversationList)}
                    rows={rowsForMode(rows, "active")}
                    onOpen={openConversation}
                    onNew={newConversation}
                    onRetry={retryConversationList}
                />
            }
        >
            {view === "context" ? (
                <ContextPanel />
            ) : activeConversationId === null ? (
                <EmptyState
                    title={t`No conversation open`}
                    body={t`Start a new conversation to make or change material with the assistant.`}
                />
            ) : (
                <ConversationCentre conversationId={activeConversationId} />
            )}
        </ChatPanel>
    );
};
